// ===========================================================================
//  Groups · discover + my groups + create
//  Route: /groups   (?create=1 auto-opens the create modal)
// ===========================================================================
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Users, Globe, Lock, Plus, Search, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const CATEGORIES = [
  "general", "gaming", "streaming", "music", "art", "sports",
  "esports", "tech", "fashion", "food", "fitness", "travel",
];

export default function Groups() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("mine");           // mine | discover | invites
  const [mine, setMine] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [invites, setInvites] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [createOpen, setCreateOpen] = useState(params.get("create") === "1");

  const load = async () => {
    try {
      if (user) {
        const [m, inv] = await Promise.all([
          api.get("/groups/mine"),
          api.get("/groups/invites/mine"),
        ]);
        setMine(m.data.items || []);
        setInvites(inv.data.items || []);
      }
      const d = await api.get("/groups/discover", { params: { q: q || undefined, category: cat !== "all" ? cat : undefined } });
      setDiscover(d.data.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => { if (tab === "discover") load(); /* eslint-disable-next-line */ }, [q, cat]);

  const acceptInvite = async (iid) => {
    try {
      const { data } = await api.post(`/groups/invites/${iid}/accept`);
      toast.success("Joined group");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const declineInvite = async (iid) => {
    try {
      await api.post(`/groups/invites/${iid}/decline`);
      toast.success("Invite declined");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="groups-page">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">// communities</div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Groups</h1>
          <p className="text-sm text-white/60 mt-1">Join member-run communities with shared rules, feeds, stories, reels and live streams.</p>
        </div>
        {user && (
          <button onClick={() => setCreateOpen(true)} className="btn-nexus !py-2.5 !px-5" data-testid="create-group-btn">
            <Plus className="w-4 h-4" /> Create group
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { id: "mine", label: `My groups${mine.length ? ` · ${mine.length}` : ""}` },
          { id: "discover", label: "Discover" },
          { id: "invites", label: `Invites${invites.length ? ` · ${invites.length}` : ""}` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-xs font-display tracking-[0.3em] uppercase transition border-b-2 whitespace-nowrap ${tab === t.id ? "border-nexus-cyan text-nexus-cyan" : "border-transparent text-white/50 hover:text-white"}`}
            data-testid={`groups-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "mine" && (
        <GroupGrid items={mine} emptyLabel={user ? "You haven't joined any groups yet." : "Sign in to see your groups."} testid="my-groups-grid" />
      )}

      {tab === "discover" && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search public groups…"
                className="w-full bg-nexus-surface border border-white/10 pl-10 pr-3 py-2 text-sm"
                data-testid="groups-search"
              />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="groups-category">
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <GroupGrid items={discover} emptyLabel="No public groups match that filter yet." testid="discover-groups-grid" />
        </>
      )}

      {tab === "invites" && (
        <div className="space-y-3" data-testid="group-invites-list">
          {invites.length === 0 && <div className="text-white/50 text-sm py-6">No pending invites.</div>}
          {invites.map((inv) => (
            <div key={inv.id} className="bg-nexus-surface border border-white/10 rounded p-4 flex items-center gap-4" data-testid={`invite-${inv.id}`}>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-nexus-violet to-nexus-cyan overflow-hidden flex-shrink-0">
                {inv.group?.cover_url && <img src={inv.group.cover_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold">{inv.group?.name || "Group"}</div>
                <div className="text-[11px] text-white/50">{inv.group?.group_type} · {inv.group?.member_count || 0} members</div>
              </div>
              <button onClick={() => acceptInvite(inv.id)} className="btn-nexus !py-1.5 !px-3 text-[10px]" data-testid={`invite-accept-${inv.id}`}>Accept</button>
              <button onClick={() => declineInvite(inv.id)} className="btn-ghost !py-1.5 !px-3 text-[10px]" data-testid={`invite-decline-${inv.id}`}>Decline</button>
            </div>
          ))}
        </div>
      )}

      {createOpen && (
        <CreateGroupModal
          onClose={() => { setCreateOpen(false); if (params.get("create")) { params.delete("create"); setParams(params); } }}
          onCreated={(g) => {
            setCreateOpen(false);
            if (params.get("create")) { params.delete("create"); setParams(params); }
            // Facebook-style: drop the owner straight into their new group.
            if (g && g.id) nav(`/group/${g.id}`);
            else { setTab("mine"); load(); }
          }}
        />
      )}
    </div>
  );
}

function GroupGrid({ items, emptyLabel, testid }) {
  if (!items.length) return <div className="text-white/50 text-sm py-10 text-center" data-testid={`${testid}-empty`}>{emptyLabel}</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testid}>
      {items.map((g) => (
        <Link key={g.id} to={`/group/${g.id}`} className="block bg-nexus-surface border border-white/10 rounded overflow-hidden group hover:border-nexus-cyan/40 transition" data-testid={`group-card-${g.id}`}>
          <div className="aspect-[3/1] bg-gradient-to-br from-nexus-violet/40 to-nexus-cyan/30 overflow-hidden">
            {g.cover_url && <img src={g.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
          </div>
          <div className="p-4 space-y-1">
            <div className="flex items-center gap-2">
              <div className="font-display font-bold text-base truncate">{g.name}</div>
              {g.privacy === "private" ? <Lock className="w-3 h-3 text-nexus-amber flex-shrink-0" /> : <Globe className="w-3 h-3 text-nexus-mint flex-shrink-0" />}
            </div>
            <div className="text-[11px] text-white/50">{g.group_type} · {g.member_count || 0} members</div>
            {g.description && <div className="text-xs text-white/60 line-clamp-2">{g.description}</div>}
            {g.my_role && <div className="inline-block text-[9px] tracking-[0.3em] uppercase bg-nexus-cyan/10 text-nexus-cyan px-2 py-0.5 rounded mt-1">{g.my_role}</div>}
          </div>
        </Link>
      ))}
    </div>
  );
}

function CreateGroupModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", group_type: "general", description: "", privacy: "public",
    age_min: 13, category: "general", rules: [""], cover_url: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const body = { ...form, rules: form.rules.map((r) => r.trim()).filter(Boolean) };
      const { data } = await api.post("/groups", body);
      toast.success(`"${data.name}" is live`);
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => !busy && onClose?.()} data-testid="create-group-modal">
      <div className="glass-strong p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow">// new community</div>
            <h3 className="font-display font-black text-2xl mt-1">Create a group</h3>
          </div>
          <button onClick={onClose} className="text-white/60"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mt-1" data-testid="create-group-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Group type · title</label>
              <input value={form.group_type} onChange={(e) => setForm({ ...form, group_type: e.target.value })} placeholder="e.g. Streaming Pros" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mt-1" data-testid="create-group-type" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm mt-1">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mt-1" data-testid="create-group-desc" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Cover image URL (optional)</label>
            <input value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://…" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Privacy</label>
              <div className="flex gap-2 mt-1">
                {["public", "private"].map((p) => (
                  <button key={p} onClick={() => setForm({ ...form, privacy: p })} className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-widest border rounded ${form.privacy === p ? "bg-nexus-cyan text-black border-nexus-cyan" : "border-white/10 text-white/60"}`} data-testid={`create-group-privacy-${p}`}>
                    {p === "public" ? <><Globe className="w-3 h-3 inline mr-1" /> Public</> : <><Lock className="w-3 h-3 inline mr-1" /> Private</>}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Age minimum</label>
              <input type="number" min={0} max={99} value={form.age_min} onChange={(e) => setForm({ ...form, age_min: parseInt(e.target.value) || 0 })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mt-1" data-testid="create-group-age" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/50">Rules (one per line)</label>
            {form.rules.map((r, i) => (
              <input
                key={i}
                value={r}
                onChange={(e) => {
                  const next = [...form.rules]; next[i] = e.target.value;
                  if (i === next.length - 1 && e.target.value.trim()) next.push("");
                  setForm({ ...form, rules: next });
                }}
                placeholder={i === 0 ? "e.g. Be kind to each other" : "Another rule…"}
                className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mt-1"
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          <button onClick={onClose} className="btn-ghost text-[10px]">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-nexus !py-2 !px-5 text-[10px]" data-testid="create-group-submit">
            {busy ? "Creating…" : "Launch group"}
          </button>
        </div>
      </div>
    </div>
  );
}
